import { useState } from "react";

import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TABLE_PAGE_SIZES } from "constants/Constants";

export function BaseTableV3Pagination({
  table,
  totalIsEstimated = false,
  extra = null,
}) {
  const {
    getCanNextPage,
    getCanPreviousPage,
    getPageCount,
    getRowCount,
    getState,
    setPageIndex,
    setPagination,
  } = table;

  const {
    pagination: { pageIndex, pageSize },
  } = getState();

  // Holds what the user is typing so a multi-digit page number does not
  // navigate on every intermediate prefix. null means "show the current page".
  const [pageDraft, setPageDraft] = useState(null);

  // Convert 0-based pageIndex to 1-based page for display
  const page = pageIndex + 1;
  const pageCount = getPageCount() || 1;
  const totalResults = getRowCount() || 0;

  // Allow arbitrary page sizes
  const pageSizeOptions = TABLE_PAGE_SIZES.includes(pageSize)
    ? TABLE_PAGE_SIZES
    : TABLE_PAGE_SIZES.concat(pageSize).sort((a, b) => a - b);

  const handleSelectPageSize = newPageSize => {
    setPagination({ pageSize: newPageSize, pageIndex: 0 });
  };

  const handlePreviousPage = () => {
    setPagination({ pageIndex: pageIndex - 1, pageSize });
  };
  const handleNextPage = () => {
    setPagination({ pageIndex: pageIndex + 1, pageSize });
  };

  const handleGoToPage = pageNum => {
    // Convert 1-based page to 0-based pageIndex
    const targetPageIndex = pageNum - 1;
    if (targetPageIndex >= 0 && targetPageIndex < pageCount) {
      setPageIndex(targetPageIndex);
    }
  };

  const handlePageInputChange = e => {
    setPageDraft(e.target.value.replace(/\D/g, ""));
  };

  // Commits on Enter and on blur only. Out-of-range entries clamp to the
  // nearest valid page rather than being discarded without feedback.
  const commitPageDraft = () => {
    if (pageDraft === null) {
      return;
    }
    setPageDraft(null);
    const parsed = parseInt(pageDraft, 10);
    if (isNaN(parsed)) {
      return;
    }
    handleGoToPage(Math.min(Math.max(parsed, 1), pageCount));
  };

  const handlePageInputKeyDown = e => {
    if (e.key === "Enter") {
      commitPageDraft();
    }
  };

  // Calculate page range for display
  const getPageRange = () => {
    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    if (pageCount <= 1) {
      return [1];
    }

    for (
      let i = Math.max(2, page - delta);
      i <= Math.min(pageCount - 1, page + delta);
      i++
    ) {
      range.push(i);
    }

    if (page - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (page + delta < pageCount - 1) {
      rangeWithDots.push("...", pageCount);
    } else if (pageCount > 1) {
      rangeWithDots.push(pageCount);
    }

    return rangeWithDots;
  };

  const pageRange = getPageRange();

  // Calculate display ranges (1-based)
  const startRange = totalResults > 0 ? (page - 1) * pageSize + 1 : 0;
  const endRange = Math.min(page * pageSize, totalResults);

  // Sized from the widest page number the table can reach, so a 5- or 6-digit
  // page neither truncates nor stretches the footer past one row.
  const pageInputWidth = `calc(${Math.max(
    String(pageCount).length,
    3
  )}ch + 26px)`;

  return (
    <div className="tw:flex tw:flex-col tw:items-stretch tw:gap-4 tw:border-t tw:border-border tw:bg-card tw:px-4 tw:py-3 tw:md:flex-row tw:md:flex-wrap tw:md:items-center tw:md:justify-between">
      <span className="tw:text-sm tw:font-medium tw:text-muted-foreground">
        {totalResults > 0 ? (
          <>
            Showing {startRange} to {endRange} of {totalIsEstimated ? "~" : ""}
            {totalResults} results
            {extra}
          </>
        ) : (
          <>
            No results
            {extra}
          </>
        )}
      </span>

      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-center tw:gap-x-8 tw:gap-y-3">
        <Pagination className="tw:mx-0 tw:w-auto">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                aria-label="Previous page"
                disabled={!getCanPreviousPage()}
                onClick={handlePreviousPage}
              />
            </PaginationItem>

            {pageRange.map((pageNum, index) => (
              // The numbered buttons are the first thing to give up when the
              // footer has to stack, as Previous/Next still cover navigation.
              <PaginationItem
                key={`page-${pageNum}-${index}`}
                className="tw:hidden tw:md:block"
              >
                {pageNum === "..." ? (
                  <PaginationEllipsis />
                ) : (
                  <PaginationLink
                    size="default"
                    className="tw:min-w-8 tw:justify-center"
                    isActive={pageNum === page}
                    aria-label={`Go to page ${pageNum}`}
                    onClick={() => handleGoToPage(pageNum)}
                  >
                    {pageNum}
                  </PaginationLink>
                )}
              </PaginationItem>
            ))}

            <PaginationItem>
              <PaginationNext
                aria-label="Next page"
                disabled={!getCanNextPage()}
                onClick={handleNextPage}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>

        <div className="tw:flex tw:items-center tw:gap-2">
          <span className="tw:text-sm tw:font-medium tw:whitespace-nowrap">
            Go to page:
          </span>
          <Input
            type="text"
            inputMode="numeric"
            className="tw:text-center"
            style={{ width: pageInputWidth }}
            value={pageDraft ?? String(page)}
            onChange={handlePageInputChange}
            onKeyDown={handlePageInputKeyDown}
            onBlur={commitPageDraft}
            aria-label="Page number"
          />
        </div>

        <div className="tw:flex tw:items-center tw:gap-2">
          <span className="tw:text-sm tw:font-medium">Show:</span>
          <Select value={pageSize} onValueChange={handleSelectPageSize}>
            <SelectTrigger aria-label="Rows per page" className="tw:min-w-24">
              <SelectValue>{value => `${value} rows`}</SelectValue>
            </SelectTrigger>
            <SelectContent alignItemWithTrigger={false}>
              <SelectGroup>
                {pageSizeOptions.map(option => (
                  <SelectItem key={option} value={option}>
                    {option} rows
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
